import { useMemo } from 'react';

import { MissionKindEnum } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import {
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT,
  PATH_MISSION_INTERNAL_ACTORS_EDIT,
} from '../../../app/constants/paths.constant';
import { buildMissionPath } from '../../../app/utils/mission-space.util';
import { useFolderAccess } from '../../Access/hooks/useFolderAccess.hook';
import { useMissionAccess } from '../../Access/hooks/useMissionAccess.hook';
import type { AccessGate } from '../../Access/types/access-gate.type';

// The level an entity takes its actors from: its folder, or the collaboration of a sub-mission.
export interface ParentActorsLevel {
  // « du dossier », « de la collaboration ».
  readonly ofLabel: string;
  readonly editPathOf: (isInternal: boolean) => string;
  // Whether the user may edit the actors of that level.
  readonly editGate: AccessGate;
}

export const useParentActorsLevel = ({
  folderId,
  collaborationId,
}: Readonly<{
  folderId: Id;
  collaborationId?: Id | null;
}>): ParentActorsLevel => {
  const folderAccess = useFolderAccess(collaborationId ? undefined : folderId);
  const collaborationAccess = useMissionAccess(collaborationId ?? undefined);

  return useMemo(
    () =>
      collaborationId
        ? {
            ofLabel: 'de la collaboration',
            editPathOf: (isInternal) =>
              buildMissionPath(
                isInternal
                  ? PATH_MISSION_INTERNAL_ACTORS_EDIT
                  : PATH_MISSION_EXTERNAL_ACTORS_EDIT,
                {
                  folderId,
                  missionId: collaborationId,
                  kind: MissionKindEnum.Composite,
                },
              ),
            editGate: collaborationAccess.update,
          }
        : {
            ofLabel: 'du dossier',
            editPathOf: (isInternal) =>
              buildPath(
                isInternal
                  ? PATH_FOLDER_INTERNAL_ACTORS_EDIT
                  : PATH_FOLDER_EXTERNAL_ACTORS_EDIT,
                { folderId },
              ),
            editGate: folderAccess.update,
          },
    [
      collaborationAccess.update,
      collaborationId,
      folderAccess.update,
      folderId,
    ],
  );
};
